import React from 'react';
import { AggregateSource } from 'hooks/useGrouping';
import {
  AssetChangePerAccount as AssetChangePerAccountInternal,
  AssetChangePerAccountProps as AssetChangePerAccountProps_,
  sortAccordionData,
  toItemsData,
} from '../../components/AssetChangePerAccount';
import {
  addSignCommaFormat,
  addSignCommaFormatPercentage,
  commaFormat,
  getSign,
} from '../../utils/formatter';
import { GetUserBrokerResultItem, getUserBroker } from 'config/api';
import { useQuery } from 'react-query';
import Loading from 'components/commons/loading/Loading';

export type AssetChangePerAccountProps = AssetChangePerAccountProps_;

export const AssetChangePerAccount = (queryPeriod: {
  from: string;
  to: string;
}) => {
  const { data: brokersData } = useQuery(
    'weeklyBrokers',
    () => fetchBrokers(queryPeriod.from, queryPeriod.to),
    {
      refetchOnWindowFocus: false,
    }
  );

  if (brokersData === null || brokersData === undefined) return <LoadingData />;
  const brokers = brokersData as GetUserBrokerResultItem[];

  const accordionData = sortAccordionData(
    brokers.map(b => {
      const ret: Parameters<typeof sortAccordionData>[0][number] = [
        {
          bankName: b.BrokerName,
          assetAmount: commaFormat(b.TotalAssetAmount ?? 0),
          comparison: addSignCommaFormat(b.DiffTotalAssetAmount ?? 0),
          aggregateSource: b.AggregateSource as AggregateSource,
          comparisonPercentage: addSignCommaFormatPercentage(
            b.DiffTotalAssetRate ?? 0,
            {
              numberOfDecimals: 2,
            }
          ),
          comparisonSign: getSign(b.DiffTotalAssetAmount ?? 0),
          isEntirePeriodData: b.IsRegisteredBefore,
          itemsData: toItemsData({
            DiffCashAmount: b.DiffCashAmount ?? 0,
            DiffCashRate: b.DiffCashRate ?? 0,
            DiffMarginProfitOrLoss: b.DiffMarginProfitOrLoss ?? 0,
            DiffMarginProfitOrLossRate: b.DiffMarginProfitOrLossRate ?? 0,
            DiffUSStockAmount: b.DiffUSStockAmount ?? 0,
            DiffUSStockRate: b.DiffUSStockRate ?? 0,
            DiffInvestmentTrustAmount: b.DiffInvestmentTrustAmount ?? 0,
            DiffInvestmentTrustRate: b.DiffInvestmentTrustRate ?? 0,
            DiffRoboAdvisorAmount: b.DiffRoboAdvisorAmount ?? 0,
            DiffRoboAdvisorRate: b.DiffRoboAdvisorRate ?? 0,
            DiffOtherAmount: b.DiffOtherAmount ?? 0,
            DiffOtherRate: b.DiffOtherRate ?? 0,
            DiffStockAmount: b.DiffStockAmount ?? 0,
            DiffStockRate: b.DiffStockRate ?? 0,
            DiffUnlistedStockAmount: b.DiffUnlistedStockAmount ?? 0,
            DiffUnlistedStockRate: b.DiffUnlistedStockRate ?? 0,
            DiffEtfEtnAmount: b.DiffEtfEtnAmount ?? 0,
            DiffEtfEtnRate: b.DiffEtfEtnRate ?? 0,
            DiffForeignEtfEtnAmount: b.DiffForeignEtfEtnAmount ?? 0,
            DiffForeignEtfEtnRate: b.DiffForeignEtfEtnRate ?? 0,
            DiffReitAmount: b.DiffReitAmount ?? 0,
            DiffReitRate: b.DiffReitRate ?? 0,
            DiffForeignReitAmount: b.DiffForeignReitAmount ?? 0,
            DiffForeignReitRate: b.DiffForeignReitRate ?? 0,
            DiffBondAmount: b.DiffBondAmount ?? 0,
            DiffBondRate: b.DiffBondRate ?? 0,
            DiffForeignBondAmount: b.DiffForeignBondAmount ?? 0,
            DiffForeignBondRate: b.DiffForeignBondRate ?? 0,
            DiffEbAmount: b.DiffEbAmount ?? 0,
            DiffEbRate: b.DiffEbRate ?? 0,
            DiffForeignEbAmount: b.DiffForeignEbAmount ?? 0,
            DiffForeignEbRate: b.DiffForeignEbRate ?? 0,
            DiffRealEstateAmount: b.DiffRealEstateAmount ?? 0,
            DiffRealEstateRate: b.DiffRealEstateRate ?? 0,
          }),
        },
        { DiffTotalAssetAmount: b.DiffTotalAssetAmount ?? 0 },
      ];
      return ret;
    })
  );

  return (
    <AssetChangePerAccountInternal
      {...{
        accordionData,
        periodVerbose: '週',
      }}
    />
  );
};

const LoadingData = () => {
  return (
    <div className="p-4 overflow-hidden">
      <div className="flex items-center mb-3 text-base font-bold text-bluegray-700">
        口座別資産変動（変動額順）
      </div>
      <Loading
        loading={true}
        circularLoading={true}
        style={{
          paddingTop: '60px',
          paddingBottom: '60px',
        }}
      />
    </div>
  );
};

const fetchBrokers = async (from: string, to: string) => {
  const res_ = await getUserBroker(undefined, from, to);
  const res = res_.Items.map(res => res);
  return res;
};
